import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import loginBg from "assets/images/login-bg.png";
import trucks from "assets/images/login/trucks.jpg";
import port from "assets/images/login/port.jpg";
import highwayRail from "assets/images/login/highway-rail.jpg";

// Bundled, Unsplash-licensed logistics imagery (port / road / rail).
const IMAGES = [loginBg, trucks, port, highwayRail];
const ROTATE_MS = 6500;

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Decorative branded panel for the login split-screen: a cross-fading carousel
 * of logistics images behind a brand-gradient overlay and the product tagline.
 * Purely ambient — aria-hidden, no controls. Auto-rotation is disabled when the
 * user prefers reduced motion (shows the first image only).
 */
export const LoginImagePanel: FC = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const reduced = useRef(prefersReducedMotion());

  useEffect(() => {
    if (reduced.current) return undefined;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % IMAGES.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="login-image-panel" aria-hidden="true">
      {IMAGES.map((src, i) => (
        <div
          key={src}
          className={`login-image-panel__layer${
            i === active ? " is-active" : ""
          }`}
          style={{ backgroundImage: `url(${src})` }}
        />
      ))}
      <div className="login-image-panel__overlay" />
      <div className="login-image-panel__copy">
        <h2>{t("loginTagline")}</h2>
        <p>{t("loginTaglineSub")}</p>
      </div>
    </div>
  );
};

export default LoginImagePanel;
